import { Injectable } from '@angular/core';
import {
    IntegrationCapability,
    IntegrationCapabilities,
    hasCapability,
    hasAnyCapability,
    hasAllCapabilities,
    getCapabilities,
    canManageSequences,
    isSequenceReadOnly
} from '../models/integration-capabilities.model';
import { Integration } from '../models/integration.model';

/**
 * Integration Capability Service
 *
 * Provides easy-to-use methods for checking what features are available
 * for each integration platform. Use this service in components to conditionally
 * show/hide functionality based on platform capabilities.
 */
@Injectable({
    providedIn: 'root'
})
export class IntegrationCapabilityService {

    constructor() { }

    /**
     * Check if an integration has a specific capability
     *
     * @example
     * if (this.capabilityService.hasCapability(integration, IntegrationCapability.SEQUENCE_MANAGEMENT)) {
     *   // Show sequence management UI
     * }
     */
    hasCapability(integration: Integration | string, capability: IntegrationCapability): boolean {
        const platformId = typeof integration === 'string' ? integration : (integration.platform || integration.id);
        return hasCapability(platformId, capability);
    }

    /**
     * Check if an integration has any of the specified capabilities
     */
    hasAnyCapability(integration: Integration | string, capabilities: IntegrationCapability[]): boolean {
        const platformId = typeof integration === 'string' ? integration : (integration.platform || integration.id);
        return hasAnyCapability(platformId, capabilities);
    }

    /**
     * Check if an integration has all of the specified capabilities
     */
    hasAllCapabilities(integration: Integration | string, capabilities: IntegrationCapability[]): boolean {
        const platformId = typeof integration === 'string' ? integration : (integration.platform || integration.id);
        return hasAllCapabilities(platformId, capabilities);
    }

    /**
     * Get all capabilities for an integration
     */
    getCapabilities(integration: Integration | string): IntegrationCapabilities | undefined {
        const platformId = typeof integration === 'string' ? integration : (integration.platform || integration.id);
        return getCapabilities(platformId);
    }

    /**
     * Check if an integration supports full sequence management (create/edit/delete)
     * Use this to show/hide sequence management buttons
     */
    canManageSequences(integration: Integration | string): boolean {
        const platformId = typeof integration === 'string' ? integration : (integration.platform || integration.id);
        return canManageSequences(platformId);
    }

    /**
     * Check if an integration is read-only for sequences
     * Use this to show read-only indicators
     */
    isSequenceReadOnly(integration: Integration | string): boolean {
        const platformId = typeof integration === 'string' ? integration : (integration.platform || integration.id);
        return isSequenceReadOnly(platformId);
    }

    /**
     * Get capability badge information for UI display
     */
    getCapabilityBadge(integration: Integration | string, capability: IntegrationCapability): {
        text: string;
        severity: 'success' | 'info' | 'warning' | 'danger';
        icon: string;
    } | null {
        if (!this.hasCapability(integration, capability)) {
            return null;
        }

        const badges: Record<IntegrationCapability, { text: string; severity: 'success' | 'info' | 'warning' | 'danger'; icon: string }> = {
            [IntegrationCapability.SEQUENCE_MANAGEMENT]: {
                text: 'Full Management',
                severity: 'success',
                icon: 'pi-cog'
            },
            [IntegrationCapability.SEQUENCE_READ_ONLY]: {
                text: 'Read Only',
                severity: 'warning',
                icon: 'pi-eye'
            },
            [IntegrationCapability.SEQUENCE_IMPORT]: {
                text: 'Import Supported',
                severity: 'info',
                icon: 'pi-download'
            },
            [IntegrationCapability.ANALYTICS_ADVANCED]: {
                text: 'Advanced Analytics',
                severity: 'success',
                icon: 'pi-chart-line'
            },
            [IntegrationCapability.REAL_TIME_SYNC]: {
                text: 'Real-time Sync',
                severity: 'success',
                icon: 'pi-refresh'
            },
            [IntegrationCapability.OAUTH_SUPPORT]: {
                text: 'OAuth',
                severity: 'info',
                icon: 'pi-lock'
            },
            [IntegrationCapability.WEBHOOK_SUPPORT]: {
                text: 'Webhooks',
                severity: 'info',
                icon: 'pi-send'
            },
            // Add more as needed
        } as any;

        return badges[capability] || {
            text: capability.replace(/_/g, ' '),
            severity: 'info',
            icon: 'pi-info-circle'
        };
    }

    /**
     * Get feature availability message for users
     */
    getFeatureAvailabilityMessage(integration: Integration | string, capability: IntegrationCapability): string {
        const platformId = typeof integration === 'string' ? integration : (integration.platform || integration.id);
        const capabilities = getCapabilities(platformId);

        if (!capabilities) {
            return `This feature is not configured for ${platformId}`;
        }

        const hasIt = this.hasCapability(integration, capability);

        if (hasIt) {
            return `This feature is available`;
        }

        // Check for limitations
        if (capabilities.limitations && capabilities.limitations.length > 0) {
            return capabilities.limitations.join('. ');
        }

        return `${capabilities.platformName} does not support this feature`;
    }

    /**
     * Check if any connected integrations support a capability
     * Useful for enabling/disabling features at the app level
     */
    anyIntegrationSupports(integrations: Integration[], capability: IntegrationCapability): boolean {
        return integrations.some(integration =>
            integration.connected &&
            integration.enabled &&
            this.hasCapability(integration, capability)
        );
    }

    /**
     * Get all integrations that support a specific capability
     */
    getIntegrationsWithCapability(integrations: Integration[], capability: IntegrationCapability): Integration[] {
        return integrations.filter(integration =>
            this.hasCapability(integration, capability)
        );
    }

    /**
     * Get user-friendly capability display name
     */
    getCapabilityDisplayName(capability: IntegrationCapability): string {
        const displayNames: Record<IntegrationCapability, string> = {
            [IntegrationCapability.SEQUENCE_MANAGEMENT]: 'Sequence Management',
            [IntegrationCapability.SEQUENCE_READ_ONLY]: 'View Sequences',
            [IntegrationCapability.SEQUENCE_IMPORT]: 'Import Sequences',
            [IntegrationCapability.CONTACT_MANAGEMENT]: 'Contact Management',
            [IntegrationCapability.CONTACT_IMPORT]: 'Import Contacts',
            [IntegrationCapability.CONTACT_EXPORT]: 'Export Contacts',
            [IntegrationCapability.ANALYTICS_BASIC]: 'Basic Analytics',
            [IntegrationCapability.ANALYTICS_ADVANCED]: 'Advanced Analytics',
            [IntegrationCapability.REAL_TIME_SYNC]: 'Real-time Sync',
            [IntegrationCapability.MESSAGE_TEMPLATES]: 'Message Templates',
            [IntegrationCapability.AI_PERSONALIZATION]: 'AI Personalization',
            [IntegrationCapability.WORKFLOW_AUTOMATION]: 'Workflow Automation',
            [IntegrationCapability.TRIGGERED_ACTIONS]: 'Triggered Actions',
            [IntegrationCapability.OAUTH_SUPPORT]: 'OAuth Authentication',
            [IntegrationCapability.API_KEY_AUTH]: 'API Key Authentication',
            [IntegrationCapability.WEBHOOK_SUPPORT]: 'Webhooks',
            [IntegrationCapability.WEBHOOK_REAL_TIME]: 'Real-time Webhooks',
        };

        return displayNames[capability] || capability.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
}
